import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../config";
import LoadingScreen from "../components/common/LoadingScreen";
import TopNav from "../components/navigation_bar/TopNav";
import SideNav from "../components/dashboard/SideNav";
import SettingsModal from "../components/dashboard/SettingsModal";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../utils/auth";
import { setStoredLangId } from "../utils/navigationHelpers";

import VocabularyDash from "../components/dashboard/vocabulary/VocabularyDash";
import TestDash from "../components/dashboard/test/TestDash";
import StorytellingDash from "../components/dashboard/storytelling/StorytellingDash";
import MusicDash from "../components/dashboard/music/MusicDash";
import AlphabetDash from "../components/dashboard/alphabet/AlphabetDash";
import ReviewDash from "../components/dashboard/review/ReviewDash";
import ListeningDash from "../components/dashboard/listening/ListeningDash";
import "./../assets/css/dashboard.css";

const getSectionFromHash = () => {
    const hash = window.location.hash.toLowerCase();
    if (hash === "#vocabulary" || hash === "#flashcards" || hash === "#flashcard") return "Flashcards";
    if (hash === "#alphabet") return "Alphabet";
    if (hash === "#testing" || hash === "#test") return "Testing";
    if (hash === "#storytelling" || hash === "#stories" || hash === "#story") return "Storytelling";
    if (hash === "#music" || hash === "#song" || hash === "#songs") return "Music";
    if (hash === "#review") return "Review";
    if (hash === "#listening") return "Listening";
    return "Flashcards";
};

function LearningDashboard() {
    const { user, activeLangId, setActiveLangId } = useAuth();
    const [registeredCourses, setRegisteredCourses] = useState([]);
    const [tiles, setTiles] = useState([]);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState(getSectionFromHash);
    const [activeLanguageId, setActiveLanguageId] = useState(() => (activeLangId ? parseInt(activeLangId, 10) : null));
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    // Ensure clean URL without query parameters
    useEffect(() => {
        if (window.location.search) {
            window.history.replaceState(null, "", window.location.pathname + window.location.hash);
        }
    }, []);

    // Sync activeSection to URL hash
    useEffect(() => {
        let hash = `#${activeSection.toLowerCase()}`;
        if (activeSection === "Testing") hash = "#testing";
        if (activeSection === "Flashcards" || activeSection === "Vocabulary") hash = "#flashcards";
        if (window.location.hash !== hash && (hash !== "#flashcards" || window.location.hash !== "#vocabulary")) {
            window.location.hash = hash;
        }
    }, [activeSection]);

    // Handle back/forward navigation in hash
    useEffect(() => {
        const handleHashChange = () => {
            setActiveSection(getSectionFromHash());
        };
        window.addEventListener("hashchange", handleHashChange);
        return () => window.removeEventListener("hashchange", handleHashChange);
    }, []);

    const loadDashboard = useCallback((langId) => {
        let url = `${API_BASE_URL}/api/dashboard_loader`;
        if (langId !== null && langId !== undefined && langId !== "") {
            url += `?lang_id=${langId}`;
        }

        return authFetch(url)
            .then((response) => response.json())
            .then((data) => {
                if (data.error) {
                    setError(true);
                    console.error('Dashboard load error:', data.error);
                    return;
                }
                setRegisteredCourses(data.registered_courses || []);
                setTiles(data.tiles || []);

                const courses = data.registered_courses || [];
                const currentRegIds = courses.map(c => parseInt(c.language_id, 10));
                if (langId === null || langId === undefined || !currentRegIds.includes(parseInt(langId, 10))) {
                    if (courses.length > 0) {
                        const firstId = parseInt(courses[0].language_id, 10);
                        setActiveLanguageId(firstId);
                        setActiveLangId(firstId);
                    } else {
                        setActiveLanguageId(null);
                    }
                } else {
                    const chosenId = parseInt(langId, 10);
                    setActiveLanguageId(chosenId);
                    setActiveLangId(chosenId);
                }
            })
            .catch(err => {
                console.error('Error loading dashboard:', err);
                setError(true);
            });
    }, [setActiveLangId]);

    useEffect(() => {
        const initialLangId = activeLangId ? parseInt(activeLangId, 10) : null;
        loadDashboard(initialLangId)
            .finally(() => {
                setLoading(false);
            });
    }, [loadDashboard, activeLangId]);

    const handleLanguageChange = (newLangId) => {
        const intId = parseInt(newLangId, 10);
        setActiveLanguageId(intId);
        setActiveLangId(intId);
        setStoredLangId(intId);
        setLoading(true);
        loadDashboard(intId)
            .finally(() => {
                setLoading(false);
            });
    };

    const handleRefreshSettings = () => {
        loadDashboard(activeLanguageId);
    };

    const renderMainContent = () => {
        if (activeLanguageId === null) {
            return (
                <section className="placeholder-section" aria-live="polite">
                    <p className="placeholder-section__text">Please add a language in Account Settings to start learning!</p>
                </section>
            );
        }

        if (activeSection === "Flashcards" || activeSection === "Vocabulary") {
            return <VocabularyDash tiles={tiles} lang_id={activeLanguageId} />;
        }
        if (activeSection === "Testing") {
            return <TestDash tiles={tiles} lang_id={activeLanguageId} />;
        }
        if (activeSection === "Alphabet") {
            return <AlphabetDash lang_id={activeLanguageId} />;
        }
        if (activeSection === "Storytelling") {
            return <StorytellingDash tiles={tiles} lang_id={activeLanguageId} />;
        }
        if (activeSection === "Music") {
            return <MusicDash lang_id={activeLanguageId} />;
        }
        if (activeSection === "Review") {
            return <ReviewDash lang_id={activeLanguageId} />;
        }
        if (activeSection === "Listening") {
            return <ListeningDash tiles={tiles} lang_id={activeLanguageId} />;
        }
        return (
            <section className="placeholder-section" aria-live="polite">
                <p className="placeholder-section__text">Content for this section is coming soon.</p>
            </section>
        );
    };

    if (!error && loading) {
        return <LoadingScreen title="Loading learning dashboard..." />;
    }

    return (
        <div className="body">
            {error && !loading && (
                <div className="error-loading-courses">
                    <h1>Error loading courses</h1>
                    <h2>There may be an issue with the server. Please refresh the page or try again later.</h2>
                </div>
            )}
            {!error && !loading && (
                <div className="learning-dashboard-root">
                    <TopNav
                        onSettingsClick={() => setIsSettingsOpen(true)}
                        registeredCourses={registeredCourses}
                        activeLanguageId={activeLanguageId}
                        onLanguageChange={handleLanguageChange}
                    />
                    <div className="learning-dashboard-shell">
                        <SideNav
                            activeItem={activeSection}
                            onChange={setActiveSection}
                            registeredCourses={registeredCourses}
                            activeLanguageId={activeLanguageId}
                            onLanguageChange={handleLanguageChange}
                            onSettingsClick={() => setIsSettingsOpen(true)}
                        />
                        <main className="learning-dashboard-main" aria-label={`${activeSection} section`}>
                            <h1 className="dashboard-page-title">{activeSection}</h1>
                            {renderMainContent()}
                        </main>
                    </div>
                    <SettingsModal
                        isOpen={isSettingsOpen}
                        onClose={() => setIsSettingsOpen(false)}
                        userId={user?.id}
                        registeredCourses={registeredCourses}
                        onRefresh={handleRefreshSettings}
                    />
                </div>
            )}
        </div>
    );
}

export default LearningDashboard;
